<template>
<div class="experiment_treatment_overview  playbook-landing">
    <!-- Loading Screen -->
    <playbook-loading-screen
        :show="if_generating"
        :playbook-id="playbook_id"
        :total-assets="1"
        :phase="'assets'"
        @navigate="handleNavigate"
    />
    <div v-show="!if_generating">
        <div class="asset-variant-detail mb-3">
            <div class="card">
                <div class="card-body">
                    <h3 class="mt-2 mb-3">Experiment Treatment Details</h3>
                    <p>This will allow you to create a new version of an existing asset to be used as an experiment treatment. You'll be able to apply behavioral psychology concepts, writing frameworks, or simply describe how the asset should be changed. To get started, provide the details below.</p>
                    <div class="row mb-3">
                        <div class="col-12">
                            <label class="form-label">Name 
                                <i class="fas fa-exclamation-circle info-icon" data-bs-toggle="tooltip" title="The name of the asset that a treatment will be built off of."></i>
                            </label>
                            <input 
                                type="text" 
                                id="playbook-name-input" 
                                class="form-control" 
                                v-model="local_asset.name" 
                                :disabled="form.busy"
                                :class="{ 'is-invalid': errors.name !== '' }" 
                                @update:modelValue="inputFieldValueChange"
                             aria-label="Text input">
                            <div class="text-danger invalid-feedback" v-if="errors.name !== ''">
                                {{ errors.name }}
                            </div>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-12">
                            <label class="form-label">Describe the Campaign & Audience
                                <i class="fas fa-exclamation-circle info-icon" data-bs-toggle="tooltip" title="Describe the audience that is engaging with this asset, what their motivations are, and what action you are trying to get them to take."></i>
                            </label>
                            <textarea 
                                class="form-control" 
                                v-model="local_asset.description" 
                                :disabled="form.busy" 
                                @update:modelValue="inputFieldValueChange" 
                                ref="textarea"
                            ></textarea>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-12">
                            <label class="form-label">Touchpoint Type
                                <i class="fas fa-exclamation-circle info-icon" data-bs-toggle="tooltip" title="Select the touchpoint type where this experiment will be deployed."></i>
                            </label>
                            <accordion-select
                                v-model="selected_channel"
                                :options="channelOptions"
                                :if_allow_edit="!form.busy"
                                name="touchpoint_type"
                                :class="{ 'is-invalid': errors.channel !== '' }"
                                @update:modelValue="selectChannel"
                            />
                            <div class="text-danger invalid-feedback" v-if="errors.channel !== ''">
                                {{ errors.channel }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="asset-variant-input-detail pb-3" v-if="local_asset.asset_type" ref="assetSection">
            <div class="card">
                <div class="card-body">
                    <h3 class="mt-2 mb-3">Original Version</h3>
                    <div 
                        v-for="(input_field, index) in local_asset.inputs" 
                        :key="`${local_asset.asset_type.id}_${index}_${input_field.placeholder}`"
                    >
                        <div class="row mb-3">
                            <div class="col-12">
                                <label class="form-label mb-0">
                                    {{ input_field.name }} <em v-if="input_field.optional === 1">(Optional)</em>
                                </label>

                                <div 
                                    :class="getErrorClasses(input_field)" 
                                    :maxlength="input_field.max_length" 
                                    :currentlength="input_field.value?.length || 0"
                                    v-if="input_field.type === 'text' || input_field.type === 'link'"
                                >
                                    <input 
                                        type="text" 
                                        v-model="input_field.value" 
                                        :maxlength="input_field.max_length" 
                                        :disabled="form.busy"
                                        class="form-control" 
                                        @update:modelValue="inputFieldValueChange"
                                     aria-label="Value">
                                </div>

                                <textarea 
                                    v-if="input_field.type === 'textarea' || input_field.type === 'template'"
                                    :id="`${input_field.placeholder}-${local_asset.playbook_asset_type_id}`" 
                                    v-model="input_field.value" 
                                    :disabled="form.busy"
                                    :maxlength="input_field.max_length" 
                                    class="form-control" 
                                    :class="{ 'is-invalid': errors[input_field.placeholder] !== '' }" 
                                    @update:modelValue="inputFieldValueChange" 
                                    ref="textarea"
                                ></textarea>

                                <div v-if="input_field.type === 'image'" class="image-array-section">
                                    <div class="d-flex flex-wrap gap-2 align-items-start">
                                        <!-- Display uploaded images as thumbnails -->
                                        <div v-for="(image_url, image_index) in getValidImages(input_field.value)" :key="image_index" class="position-relative image-thumbnail-wrapper" style="max-width: 200px;">
                                            <img 
                                                :src="image_url" 
                                                alt="Uploaded image" 
                                                class="image-thumbnail"
                                                style="height: 100px; width: auto; max-width: 100%; object-fit: contain; border-radius: 20px; cursor: pointer;"
                                                @click.prevent="openImagePreview(image_index, input_field)"
                                            />
                                            <div class="position-absolute top-0 end-0 d-flex gap-1 m-1">
                                                <button 
                                                    type="button" 
                                                    class="btn btn-icon-only-sm btn-secondary p-1 mt-1" 
                                                    data-bs-toggle="tooltip" title="Remove"
                                                    @click.stop.prevent="removeImage(image_index, input_field)"
                                                    :disabled="form.busy"
                                                    style="display: flex; align-items: center; justify-content: center;"
                                                >
                                                    <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <!-- Plus button to add new image -->
                                        <button 
                                            type="button" 
                                            class="btn btn-secondary d-flex align-items-center justify-content-center add-image-btn"
                                            data-bs-toggle="tooltip" title="Add Image"
                                            @click.prevent="openImageModal(input_field)"
                                            :disabled="form.busy"
                                            style="width: 100px; height: 100px; border-radius: 20px;"
                                        >
                                            <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="Add image" style="width: 24px; height: 24px;">
                                        </button>
                                    </div>
                                    
                                    <!-- Image Upload Modal - Teleported to body for nested modal support -->
                                    <Teleport to="body">
                                        <div class="modal fade" :id="'experiment-image-upload-modal-' + input_field.placeholder" tabindex="-1" :aria-labelledby="'experiment-image-upload-modal-label-' + input_field.placeholder" aria-hidden="true" data-bs-backdrop="static">
                                            <div class="modal-dialog modal-dialog-centered">
                                                <div class="modal-content">
                                                    <div class="modal-header border-bottom-0 pb-0">
                                                        <h5 class="modal-title" :id="'experiment-image-upload-modal-label-' + input_field.placeholder">Upload Image</h5>
                                                        <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close">
                                                            <img class="icon" src="/img/icons/avid-icons/close.svg" alt="">
                                                        </button>
                                                    </div>
                                                    <div class="modal-body">
                                                        <ImageDropzone
                                                            v-if="modalImageResetKeys[input_field.placeholder] !== undefined"
                                                            :key="'experiment-image-dropzone-' + input_field.placeholder + '-' + modalImageResetKeys[input_field.placeholder]"
                                                            v-model="modalImageValues[input_field.placeholder]"
                                                            class="image-dropzone-wrapper"
                                                            :id="'modal-experiment-image-dropzone-' + input_field.placeholder"
                                                            :disabled="form.busy"
                                                            :if_thumbnail="false"
                                                            @update:modelValue="(value) => handleModalImageUpdate(value, input_field)"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <!-- Image Preview Modal - Teleported to body for nested modal support -->
                                        <div class="modal fade" :id="'experiment-image-preview-modal-' + input_field.placeholder" tabindex="-1" :aria-labelledby="'experiment-image-preview-modal-label-' + input_field.placeholder" aria-hidden="true" data-bs-backdrop="static">
                                            <div class="modal-dialog modal-dialog-centered modal-lg">
                                                <div class="modal-content">
                                                    <div class="modal-header border-bottom-0 pb-0">
                                                        <h5 class="modal-title" :id="'experiment-image-preview-modal-label-' + input_field.placeholder">
                                                            Preview
                                                        </h5>
                                                        <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close" @click="closeImagePreviewModal(input_field)">
                                                            <img class="icon" src="/img/icons/avid-icons/close.svg" alt="">
                                                        </button>
                                                    </div>
                                                    <div class="modal-body px-4 pb-4">
                                                        <!-- Image Display -->
                                                        <div v-if="previewImageUrls[input_field.placeholder]" class="d-flex justify-content-center align-items-center" style="min-height: 400px;">
                                                            <img 
                                                                :src="previewImageUrls[input_field.placeholder]" 
                                                                alt="Preview image" 
                                                                class="img-fluid"
                                                                style="max-width: 100%; max-height: 70vh; object-fit: contain; border-radius: 20px;"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Teleport>
                                </div>

                                <FileDropzone 
                                    v-if="input_field.type === 'file'" 
                                    :client-url="client.url"
                                    :on-file-uploaded="(file) => handleFileUploaded(file, input_field)"
                                />

                                <wysiwyg-editor-advanced 
                                    v-if="input_field.type === 'wysiwyg'" 
                                    :input_value="input_field.value" 
                                    :input_index="index" 
                                    :asset_id="null"
                                    @wysiwygValueChange="wysiwygValueChange" 
                                    :if_allow_edit="!form.busy"
                                ></wysiwyg-editor-advanced>

                                <select 
                                    v-if="input_field.type === 'dropdown'" 
                                    v-model="input_field.value" 
                                    :disabled="form.busy" 
                                    class="form-select w-auto form-control"
                                    :class="{ 'is-invalid': errors[input_field.placeholder] !== '' }" 
                                    @update:modelValue="inputFieldValueChange"
                                 aria-label="Value">
                                    <option value="" disabled selected>Select the value...</option>
                                    <option 
                                        v-for="option in input_field.options" 
                                        :key="option" 
                                        :value="option"
                                    >
                                        {{ option }}
                                    </option>
                                </select>
                            </div>
                            <div class="text-danger invalid-feedback" 
                                v-if="errors[input_field.placeholder] != null && errors[input_field.placeholder] !== ''"
                                v-html="errors[input_field.placeholder]"
                            ></div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col">
                            <div class="alert alert-danger mb-3" v-if="if_has_errors">
                                Fill out all required fields above including name before creating a treatment.
                            </div>
                            <div class="mt-3">
                                <regenerate-button
                                    :asset="local_asset"
                                    :client="client"
                                    :concepts="concepts"
                                    :disabled="form.busy || !areAllRequiredFieldsFilled"
                                    :if_generate_treatment="true"
                                    @treatment-generated="handleTreatmentGenerated"
                                />                                
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
    import { defineComponent } from 'vue';
    import ImageDropzone from '../components/ImageDropzone.vue';
    import FileDropzone from '../components/FileDropzone.vue';
    import PlaybookLoadingScreen from './PlaybookLoadingScreen.vue';
    import RegenerateButton from './RegenerateButton.vue';
    import WysiwygEditorAdvanced from '../components/WysiwygEditorAdvanced.vue';
    import AccordionSelect from '../components/AccordionSelect.vue';
    import { usePlaybookStore } from '../../stores/playbook';

    export default defineComponent({
        name: 'ExperimentTreatmentOverview',
        components: {
            ImageDropzone,
            FileDropzone,
            PlaybookLoadingScreen,
            RegenerateButton,
            WysiwygEditorAdvanced,
            AccordionSelect
        },
        setup() {
            const playbookStore = usePlaybookStore();
            return { playbookStore };
        },
        props: {
            client: {
                type: Object,
                required: true
            },
            asset_types: {
                type: Array,
                required: true
            },
            concepts: {
                type: Array,
                required: true
            }
        },
        data() {
            return {
                local_asset:{
                    inputs:[]
                },
                is_changes_made: false,
                if_has_errors: false,
                is_concept_expanded: false,
                provided_example: null,
                form: {
                    successful: false,
                    busy: false,
                    error: false
                },
                errors: {
                    name:'',
                    channel:'', // consolidated channel/asset type error
                },
                // Loading state properties
                if_generating: false,
                playbook_id: null,
                playbook_url: null,
                // Search functionality
                search_key: '',
                // Channel selection
                selected_channel: null,
                // Image modal state
                modalImageValues: {},
                modalImageResetKeys: {},
                previewImageUrls: {},
                previewImageIndices: {},
            };
        },
        computed: {
            playbook() {
                return this.playbookStore.getSelectedPlaybook;
            },
            channelOptions() {
                // Transform asset types into accordion-select format
                return this.asset_types.map(asset_type => ({
                    value: asset_type.id,
                    text: asset_type.name,
                    category: '',
                    icon_class: asset_type.icon_class
                }));
            },
            areAllRequiredFieldsFilled() {
                // Check name
                if (!this.local_asset.name || this.local_asset.name.trim() === '') {
                    return false;
                }
                
                // Check asset type (channel)
                if (!this.local_asset.asset_type) {
                    return false;
                }
                
                // Check description
                if (!this.local_asset.description || this.local_asset.description.trim() === '') {
                    return false;
                }
                
                // Check all required input fields
                if (this.local_asset.inputs && this.local_asset.inputs.length > 0) {
                    for (const input of this.local_asset.inputs) {
                        // Only check non-optional inputs for text, textarea, wysiwyg types
                        if (input.optional === 0 && (input.type === 'text' || input.type === 'textarea' || input.type === 'wysiwyg')) {
                            if (!input.value || input.value.trim() === '') {
                                return false;
                            }
                        }
                    }
                }
                
                return true;
            }
        },
        beforeMount() {
            // Always create a new playbook since it will never exist on this page
            const newPlaybook = {
                client_id: this.playbook.client_id,
                name: '',
                playbook_type_id: this.playbook.playbook_type_id,
                prompts: [],
                asset_types: [],
                assets: []
            };
            
            this.playbookStore.setSelectedPlaybook(newPlaybook);

            this.local_asset = {
                id: Math.round(Math.random() * -10000), // Temporary ID for modals
                client_id: this.playbook.client_id,
                name: '',
                playbook_id: -1,
                asset_types: null,
                playbook_asset_type_id: -1,
                description: '',
                inputs: null,
                status: 'draft', // default to draft
                is_active: true
            };
        },
        mounted() {
            this.initTooltips();
        },
        beforeUnmount() {
            // Clean up image modals
            Object.keys(this.modalImageValues).forEach(placeholder => {
                const uploadModalId = 'experiment-image-upload-modal-' + placeholder;
                const uploadModalElement = document.getElementById(uploadModalId);
                if (uploadModalElement) {
                    const uploadModal = window.bootstrap.Modal.getInstance(uploadModalElement);
                    if (uploadModal) {
                        uploadModal.hide();
                        uploadModal.dispose();
                    }
                }
                
                const previewModalId = 'experiment-image-preview-modal-' + placeholder;
                const previewModalElement = document.getElementById(previewModalId);
                if (previewModalElement) {
                    const previewModal = window.bootstrap.Modal.getInstance(previewModalElement);
                    if (previewModal) {
                        previewModal.hide();
                        previewModal.dispose();
                    }
                }
            });
        },
        methods: {
            hideAllModals() {
                const modals = document.querySelectorAll('.modal.show');
                modals.forEach(modal => {
                    const modalInstance = window.bootstrap.Modal.getInstance(modal);
                    if (modalInstance) {
                        modalInstance.hide();
                    }
                });
            },
            initTooltips() {
                const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
                const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

                for (var i = 0; i < tooltipTriggerList.length; i++) {
                  (function(index) {
                    tooltipTriggerList[index].addEventListener('click', function() {
                      tooltipList[index].hide();
                    });
                    tooltipTriggerList[index].addEventListener('mouseleave', function() {
                      tooltipList[index].hide();
                    });
                  })(i);
                }
            },
            selectChannel(channelValue) {
                if (this.form.busy || !channelValue) {
                    return;
                }
                const asset_type = this.asset_types.find(at => at.id === channelValue.value);
                if (asset_type) {
                    this.local_asset.asset_type = asset_type;
                    this.local_asset.playbook_asset_type_id = asset_type.id
                    
                    // Also update selected_channel to keep them in sync
                    this.selected_channel = {
                        value: asset_type.id,
                        text: asset_type.name,
                        category: '',
                        icon_class: asset_type.icon_class
                    };
                    this.loadInputs();
                }
            },
            inputFieldValueChange(){
                this.is_changes_made = true;
            },
            getValidImages(imageArray) {
                if (!Array.isArray(imageArray)) {
                    return [];
                }
                return imageArray.filter(img => img !== null && img !== '' && img !== undefined);
            },
            openImageModal(input_field) {
                // Initialize if needed
                if (!(input_field.placeholder in this.modalImageValues)) {
                    this.modalImageValues[input_field.placeholder] = '';
                }
                if (!(input_field.placeholder in this.modalImageResetKeys)) {
                    this.modalImageResetKeys[input_field.placeholder] = 0;
                }
                
                this.modalImageValues[input_field.placeholder] = '';
                this.modalImageResetKeys[input_field.placeholder] += 1;
                
                this.$nextTick(() => {
                    const modalId = 'experiment-image-upload-modal-' + input_field.placeholder;
                    const modalElement = document.getElementById(modalId);
                    if (modalElement) {
                        const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                        if (existingModal) {
                            existingModal.dispose();
                        }
                        
                        const modal = new window.bootstrap.Modal(modalElement, {
                            backdrop: 'static',
                            keyboard: false,
                            focus: true
                        });
                        
                        const handleHidden = () => {
                            this.modalImageValues[input_field.placeholder] = '';
                            modalElement.removeEventListener('hidden.bs.modal', handleHidden);
                        };
                        modalElement.addEventListener('hidden.bs.modal', handleHidden);
                        
                        modal.show();
                        
                        this.$nextTick(() => {
                            const tooltipTriggerList = modalElement.querySelectorAll('[data-bs-toggle="tooltip"]');
                            [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                                const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                                if (existingTooltip) {
                                    existingTooltip.dispose();
                                }
                                new window.bootstrap.Tooltip(tooltipTriggerEl);
                            });
                        });
                    }
                });
            },
            handleModalImageUpdate(value, input_field) {
                if (value && value !== '') {
                    value = value.replace("%2F", "/");
                    value = value.startsWith("http") ? value : 'https:' + value;
                    
                    if (!Array.isArray(input_field.value)) {
                        input_field.value = [];
                    }
                    
                    input_field.value.push(value);
                    
                    const modalId = 'experiment-image-upload-modal-' + input_field.placeholder;
                    const modalElement = document.getElementById(modalId);
                    if (modalElement) {
                        const modal = window.bootstrap.Modal.getInstance(modalElement);
                        if (modal) {
                            modal.hide();
                        }
                    }
                    
                    this.modalImageValues[input_field.placeholder] = '';
                    this.modalImageResetKeys[input_field.placeholder] += 1;
                    this.inputFieldValueChange();
                }
            },
            removeImage(image_index, input_field) {
                const validImages = this.getValidImages(input_field.value);
                if (Array.isArray(input_field.value) && validImages.length > 0) {
                    let validIndex = 0;
                    for (let i = 0; i < input_field.value.length; i++) {
                        if (input_field.value[i] !== null && input_field.value[i] !== '' && input_field.value[i] !== undefined) {
                            if (validIndex === image_index) {
                                input_field.value.splice(i, 1);
                                break;
                            }
                            validIndex++;
                        }
                    }
                    
                    if (input_field.value.length === 0) {
                        input_field.value = [];
                    }
                    
                    this.inputFieldValueChange();
                    this.$forceUpdate();
                }
            },
            openImagePreview(image_index, input_field) {
                const validImages = this.getValidImages(input_field.value);
                if (validImages.length > 0 && image_index < validImages.length) {
                    this.previewImageUrls[input_field.placeholder] = validImages[image_index];
                    this.previewImageIndices[input_field.placeholder] = image_index;
                    
                    this.$nextTick(() => {
                        const modalId = 'experiment-image-preview-modal-' + input_field.placeholder;
                        const modalElement = document.getElementById(modalId);
                        if (modalElement) {
                            const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                            if (existingModal) {
                                existingModal.dispose();
                            }
                            
                            const modal = new window.bootstrap.Modal(modalElement, {
                                backdrop: 'static',
                                keyboard: false,
                                focus: true
                            });
                            
                            const handleHidden = () => {
                                this.closeImagePreviewModal(input_field);
                                modalElement.removeEventListener('hidden.bs.modal', handleHidden);
                            };
                            modalElement.addEventListener('hidden.bs.modal', handleHidden);
                            
                            modal.show();
                            
                            this.$nextTick(() => {
                                const tooltipTriggerList = modalElement.querySelectorAll('[data-bs-toggle="tooltip"]');
                                [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                                    const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                                    if (existingTooltip) {
                                        existingTooltip.dispose();
                                    }
                                    new window.bootstrap.Tooltip(tooltipTriggerEl);
                                });
                            });
                        }
                    });
                }
            },
            closeImagePreviewModal(input_field) {
                delete this.previewImageUrls[input_field.placeholder];
                delete this.previewImageIndices[input_field.placeholder];
                
                const modalId = 'experiment-image-preview-modal-' + input_field.placeholder;
                const modalElement = document.getElementById(modalId);
                if (modalElement) {
                    const modal = window.bootstrap.Modal.getInstance(modalElement);
                    if (modal) {
                        modal.hide();
                    }
                }
            },
            toggleBehavioralConceptOptions(){
                this.is_concept_expanded = !this.is_concept_expanded;
            },
            wysiwygValueChange(newValueWithIndex){
                this.local_asset.inputs.forEach((input, index) => {
                    if (input.type=='wysiwyg' && index == newValueWithIndex.index){
                        if (this.local_asset.inputs[index].value != newValueWithIndex.value){
                            this.is_changes_made = true;
                        }
                        this.local_asset.inputs[index].value = newValueWithIndex.value;
                    }
                });
            },
            loadInputs(){
                this.local_asset.inputs = [];
                this.local_asset.asset_type.inputs.forEach(input => {
                    var val = null;
                    if(this.local_asset.inputs != null) {
                        let ph = this.local_asset.inputs.find(i => i.placeholder == input.placeholder);
                        if(ph != null)
                            val = ph.value;
                    }

                    var input_object = {
                        name: input.name,
                        description:input.description,
                        max_length:input.max_length,
                        placeholder:input.placeholder,
                        type:input.type,
                        optional:input.optional,
                        options:input.options,
                        value: val,
                        orig_value: val,
                    }
                    if(input.type=='image'){ // construct image array
                        if (!Array.isArray(val)){
                            input_object.value = [val];
                            input_object.orig_value = [val];
                        }
                    }
                    this.local_asset.inputs.push(input_object);

                    if(input.max_length != null && val != null && input.max_length < val.length){
                        this.errors[input.placeholder] = 'The maximum number of characters is ' + input.max_length + ". When generating text, the AI has a hard time following character limit rules&mdash;you'll need to adjust."
                    }
                    else
                        this.errors[input.placeholder]='';

                });
            },
            hasErrors(){
                if ((this.local_asset.name == null || this.local_asset.name == "")){
                    this.errors.name = 'Please provide a name for the asset that is being tested.'
                } else {
                    this.errors.name = '';
                }

                // Validate channel selection (asset type)
                if(!this.local_asset.asset_type){
                    this.errors.channel = 'Please select a Touchpoint Type for the asset.';
                } else {
                    this.errors.channel = '';
                }

                this.local_asset.inputs.forEach((input) =>{
                    if((input.value == null || input.value == "") && (input.optional == 0) && (input.type == 'text' || input.type == 'textarea' || input.type=='wysiwyg')){
                        this.errors[input.placeholder] = input.name + " is required.";
                    } else if (input.value != null && input.max_length != null && input.value.length > input.max_length){
                        this.errors[input.placeholder] = 'The maximum number of characters is ' + input.max_length + ". Please adjust the text above."
                    } else {
                        this.errors[input.placeholder] = "";
                    }
                })
                for (const error_name in this.errors) {
                    if (this.errors[error_name] != '') {
                        return true;
                    }
                }
                return false;
            },
            getErrorClasses(input) {
                var classes = ''

                if (input.max_length != null)
                    classes += 'max-warning'

                if(this.errors[input.placeholder] != null && this.errors[input.placeholder] != '' )
                    classes +=' is-invalid';

                return classes;
            },
            handleFileUploaded(file, input_field) {
                input_field.value = file;
                this.inputFieldValueChange();
            },
            handleTreatmentGenerated(playbook) {
                // Start the loading state
                this.if_generating = true;
                this.playbook_id = playbook.id;
                this.playbook_url = playbook.url;

                //Close all modals 
                this.hideAllModals();
            },
            handleNavigate() {
                // Navigate to playbook builder with Touchpoints tab
                window.location = `/playbooks/${this.client.url}/builder/${this.playbook_url}/touchpoints`;
            },
        }
    })
</script>
